import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { adminMe } from "@/lib/api";
import { LogOut, LayoutDashboard, UtensilsCrossed, Calendar, Image, Inbox, ShoppingCart, Settings as SettingsIcon } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const LINKS = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/menu", label: "Menu", icon: UtensilsCrossed },
    { to: "/admin/events", label: "Events", icon: Calendar },
    { to: "/admin/gallery", label: "Gallery", icon: Image },
    { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
    { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("smokin_admin_token");
        if (!token) { navigate("/admin/login", { replace: true }); return; }
        adminMe()
            .then(setMe)
            .catch(() => { localStorage.removeItem("smokin_admin_token"); navigate("/admin/login", { replace: true }); })
            .finally(() => setChecking(false));
    }, [navigate]);

    const logout = () => {
        localStorage.removeItem("smokin_admin_token");
        navigate("/admin/login", { replace: true });
    };

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="flame-loader" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex relative" data-testid="admin-layout">
            <aside className="hidden md:flex flex-col w-64 border-r border-white/10 bg-black/70 sticky top-0 h-screen" data-testid="admin-sidebar-nav">
                <Link to="/admin" className="flex items-center gap-3 px-5 py-5 border-b border-white/10 logo-glow">
                    <BrandLogo size={36} />
                    <div>
                        <div className="font-display text-lg tracking-[0.06em]">SMOKIN&rsquo; MO&rsquo;S</div>
                        <div className="text-[10px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                    </div>
                </Link>
                <nav className="flex-1 p-3 space-y-1">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                                    isActive ? "bg-orange-500/15 text-orange-300" : "text-white/75 hover:bg-white/5"
                                }`
                            }
                            data-testid={`admin-nav-${l.label.toLowerCase()}`}
                        >
                            <l.icon size={16} /> {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10">
                    <div className="text-xs text-white/55 mb-2">Signed in as <span className="text-white">{me?.username}</span></div>
                    <button onClick={logout} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5" data-testid="admin-logout-button">
                        <LogOut size={16}/> Log out
                    </button>
                </div>
            </aside>
            <div className="flex-1 min-w-0">
                {/* Mobile top bar */}
                <div className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-black/80 backdrop-blur" data-testid="admin-mobile-topbar">
                    <Link to="/admin" className="flex items-center gap-2">
                        <BrandLogo size={28} />
                        <span className="font-display tracking-[0.06em] text-sm">SMOKIN&rsquo; MO&rsquo;S Admin</span>
                    </Link>
                    <button onClick={logout} className="p-2 rounded-md border border-white/10" aria-label="Log out" data-testid="admin-mobile-logout">
                        <LogOut size={16}/>
                    </button>
                </div>
                <div className="md:hidden flex flex-wrap gap-2 px-4 pt-3" data-testid="admin-mobile-tabs">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.end}
                            className={({ isActive }) =>
                                `flex items-center gap-1 px-3 py-1.5 rounded-md text-xs uppercase tracking-[0.18em] font-display border ${
                                    isActive ? "bg-orange-500/20 text-orange-300 border-orange-400/40" : "text-white/75 border-white/10"
                                }`
                            }
                            data-testid={`admin-mobile-nav-${l.label.toLowerCase()}`}
                        >
                            <l.icon size={14} /> {l.label}
                        </NavLink>
                    ))}
                </div>
                <main className="p-4 sm:p-6 lg:p-8" data-testid="admin-main">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
